import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/** Strop zásobníka — hlbšie sa v praxi nikto nevracia. */
export const MAX_KROKOV = 20;

export interface UndoKrok {
  /** Čo sa vráti — ide do tlačidla aj do toastu. */
  popis: string;
  /** Inverzia akcie. Hodí výnimku, ak sa vrátiť nedá. */
  vrat: () => Promise<void>;
}

export interface UndoApi {
  pridaj: (krok: UndoKrok) => void;
  /** Vráti popis vráteného kroku, alebo `null` ak nebolo čo vrátiť. */
  vrat: () => Promise<string | null>;
  /** Popis kroku na vrchu zásobníka (do `title` tlačidla). */
  posledny: string | null;
  pocet: number;
  bezi: boolean;
}

const UndoCtx = createContext<UndoApi | null>(null);

/**
 * Zásobník vrátení pre editor zákazky (Ctrl+Z).
 *
 * Je to **zásobník vlastných posledných zmien v tejto karte**, nie revert
 * cudzích zmien cez históriu — to je iná vec s inými rizikami. Preto žije
 * v pamäti a **neprežije reload**; napísané je to v dialógu skratiek, aby
 * to nebolo prekvapenie.
 *
 * Provider sedí v editore, nie v `App` — mimo editora by kroky viseli bez
 * kontextu. Zásobník sa **vyprázdni pri zmene zákazky**: krok, ktorý
 * ukazuje na inú zákazku, je pasca.
 */
export function UndoProvider({
  zakazkaId,
  children,
}: {
  zakazkaId: string;
  children: ReactNode;
}): JSX.Element {
  const [kroky, setKroky] = useState<UndoKrok[]>([]);
  const [bezi, setBezi] = useState(false);
  // Vrátenie číta zásobník v callbacku, takže potrebuje aktuálnu hodnotu
  // bez toho, aby sa `vrat` menil pri každom pridaní kroku.
  const zasobnik = useRef<UndoKrok[]>([]);
  zasobnik.current = kroky;

  useEffect(() => {
    setKroky([]);
  }, [zakazkaId]);

  const pridaj = useCallback((krok: UndoKrok) => {
    setKroky((prev) => [...prev, krok].slice(-MAX_KROKOV));
  }, []);

  const vrat = useCallback(async (): Promise<string | null> => {
    const krok = zasobnik.current[zasobnik.current.length - 1];
    if (!krok) return null;
    setBezi(true);
    // Krok sa vyhodí zo zásobníka **pred** pokusom, nie po ňom: keď
    // vrátenie zlyhá (medzitým zmizol rodič), Ctrl+Z by inak narážal do
    // tej istej steny dookola.
    setKroky((prev) => prev.slice(0, -1));
    try {
      await krok.vrat();
      return krok.popis;
    } finally {
      setBezi(false);
    }
  }, []);

  const api = useMemo<UndoApi>(
    () => ({
      pridaj,
      vrat,
      posledny: kroky.length ? kroky[kroky.length - 1]!.popis : null,
      pocet: kroky.length,
      bezi,
    }),
    [pridaj, vrat, kroky, bezi],
  );

  return <UndoCtx.Provider value={api}>{children}</UndoCtx.Provider>;
}

/** Undo API vnútri providera. Vyhodí, ak provider chýba. */
export function useUndo(): UndoApi {
  const ctx = useContext(UndoCtx);
  if (!ctx) throw new Error("useUndo musí byť vnútri UndoProvider");
  return ctx;
}

/**
 * Undo API, alebo `null` mimo providera.
 *
 * Mutačné hooky používajú túto verziu — tie isté hooky volajú aj dialógy
 * a stránky mimo editora, kde sa krok jednoducho nezapíše.
 */
export function useUndoOptional(): UndoApi | null {
  return useContext(UndoCtx);
}
